import AppStore from '@v4/store/appStore'
import { useMemo } from 'react'
import { MERGE_TAG_GROUP } from '@v4/hooks/useEditor'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageData from '@v4/utils/data/PageData'
import QuizQuestionsSelector from '@v4/utils/selectors/QuizQuestionsSelector'
import usePlainTextReplacer from '@v4/hooks/usePlainTextReplacer'

const useQuizMergeTags = () => {
	const pageType = PageBuilderSelector.useType()

	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const questionIds = QuizQuestionsSelector.useQuestionIds()
	const questionEntities = QuizQuestionsSelector.useQuestionEntities()
	const plainTextReplacer = usePlainTextReplacer()

	return useMemo(() => {
		if (!isBuilderMode || (!PageData.isTypeResult(pageType) && !PageData.isTypeResultPdf(pageType))) {
			return undefined
		}

		const getMergeTagData = ({ ref, question, answer, score, index, value, answerType }) => {
			const group = `quiz_question:${ref}`
			const groups = {
				[group]: {
					title: question,
					order: index + 1,
				},
			}
			const tags = []
			const shortcuts = []

			const questionKey = `quiz_question.${ref}.question`
			tags[questionKey] = {
				title: 'Question',
				searchLabel: `Question | ${question}`,
				label: `Question | ${question}`,
				groups: [MERGE_TAG_GROUP.QUIZ_ANSWER, group],
				order: 1,
			}
			shortcuts[questionKey] = question

			const answerKey = `quiz_question.${ref}.answer`
			tags[answerKey] = {
				title: 'Answer',
				searchLabel: `Answer | ${question}`,
				label: `Answer | ${question}`,
				groups: [MERGE_TAG_GROUP.QUIZ_ANSWER, group],
				order: 2,
			}
			shortcuts[answerKey] = answer

			const scoreKey = `quiz_question.${ref}.score`
			tags[scoreKey] = {
				title: 'Score',
				searchLabel: `Score | ${question}`,
				label: `Score | ${question}`,
				groups: [MERGE_TAG_GROUP.QUIZ_ANSWER, group],
				order: 3,
			}
			shortcuts[scoreKey] = score

			if (_.includes(['yesno', 'multiplechoice'], answerType)) {
				const valueKey = `quiz_question.${ref}.answer_value`
				tags[valueKey] = {
					title: 'Value',
					searchLabel: `Value | ${question}`,
					label: `Value | ${question}`,
					groups: [MERGE_TAG_GROUP.QUIZ_ANSWER, group],
					order: 4,
				}
				shortcuts[valueKey] = value || answer
			}

			return {
				groups,
				tags,
				shortcuts,
			}
		}

		return _.reduce(
			questionIds,
			(acc, id, index) => {
				const { question, ref, answer_type: answerType } = questionEntities[id] || {}
				if (!question || !ref || answerType === 'placeholder') {
					return acc
				}
				const answer = `("${question}" answer)`
				const value = `("${question}" value)`
				const { groups, tags, shortcuts } = getMergeTagData({
					ref,
					question: plainTextReplacer(question),
					answer: plainTextReplacer(answer),
					value: plainTextReplacer(value),
					score: 1,
					index,
					answerType,
				})
				acc.groups = { ...acc.groups, ...groups }
				acc.tags = { ...acc.tags, ...tags }
				acc.shortcuts = { ...acc.shortcuts, ...shortcuts }

				return acc
			},
			{
				tags: {},
				groups: {},
				shortcuts: {},
			},
		)
	}, [isBuilderMode, pageType, plainTextReplacer, questionEntities, questionIds])
}

export default useQuizMergeTags
